/* eslint-disable import/prefer-default-export */
import { useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { useNativeAuth } from "context/NativeAuthContext";
import { siteData } from "authConfig";

/**
 * File download/upload with the same auth as useFetchWithMsal, but for binary/multipart transfers
 * the shared JSON hook can't do (it JSON-stringifies the body and JSON-parses every response).
 * Native token first, MSAL silent token as fallback — mirrors useFetchWithMsal's token order.
 */
function scopeList() {
  const s = siteData?.apiScopes;
  if (Array.isArray(s)) return s;
  return s ? [s] : [];
}

export function useAuthedFileApi() {
  const { instance } = useMsal();
  const { getValidAccessToken } = useNativeAuth();

  const getToken = useCallback(async () => {
    let token = await getValidAccessToken();
    if (!token) {
      const account = instance.getActiveAccount();
      const scopes = scopeList();
      if (account && scopes.length) {
        const silent = await instance.acquireTokenSilent({ scopes, account });
        token = silent?.accessToken;
      }
    }
    return token;
  }, [getValidAccessToken, instance]);

  // GET a file and trigger a browser download. Throws with a useful message on error.
  const download = useCallback(
    async (url, filename) => {
      const token = await getToken();
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const ct = res.headers.get("content-type") || "";
      if (!res.ok || ct.includes("application/json")) {
        let msg = `Download failed (${res.status}).`;
        try {
          const j = await res.json();
          if (j?.message) msg = j.message;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
    },
    [getToken]
  );

  // POST a single file as multipart/form-data (field name "file"). Returns { ok, status, ...json }.
  const upload = useCallback(
    async (url, file) => {
      const token = await getToken();
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      let json = {};
      try {
        json = await res.json();
      } catch {
        json = {};
      }
      return { ok: res.ok, status: res.status, ...json };
    },
    [getToken]
  );

  return { download, upload };
}
